// Éléments partagés du module « controle » : constats, sources, blocs de résultats, modes des services.
import { useState, type ReactNode } from "react";
import { Badge, SimBadge, Source, StatusChip, type CheckStatus } from "@core/ui";
import { date } from "@core/format";

export type SourceRef = { document_id: string | null; label: string; page: number | null; detail: string };

export type Check = {
  id: string;
  applicant_id: string | null;
  personne: string | null;
  document_id: string | null;
  family: string;
  family_label: string;
  block: "completude" | "documentaire" | "criteres";
  code: string;
  status: CheckStatus;
  title: string;
  explanation: string;
  sources: SourceRef[];
  details: Record<string, any>;
  rule_version: string;
  created_at: string;
};

export type Block = {
  block: "completude" | "documentaire" | "criteres";
  title: string;
  state: string;
  state_label: string;
  scope: string;
  counts: Record<CheckStatus, number>;
  checks: Check[];
};

export type Modes = Record<string, { nom: string; mode: string } | null>;

export const MODE_LABELS: Record<string, string> = {
  lecture_pieces: "Lecture des pièces",
  repertoire_entreprises: "Répertoire des entreprises",
  liste_confiance_2d_doc: "Liste de confiance 2D-DOC",
};

const STATUS_WORDS: Record<CheckStatus, [string, string]> = {
  conforme: ["conforme", "conformes"],
  a_confirmer: ["à confirmer", "à confirmer"],
  anomalie: ["anomalie détectée", "anomalies détectées"],
  manquant: ["pièce manquante", "pièces manquantes"],
  info: ["information", "informations"],
  non_concluant: ["non concluant", "non concluants"],
};

/** « 3 conformes · 1 à confirmer » : décompte par statut, jamais une note. */
export function countsText(counts: Partial<Record<CheckStatus, number>>): string {
  const order: CheckStatus[] = ["manquant", "anomalie", "a_confirmer", "non_concluant", "conforme", "info"];
  const parts = order.filter((s) => (counts[s] ?? 0) > 0).map((s) => `${counts[s]} ${STATUS_WORDS[s][(counts[s] ?? 0) > 1 ? 1 : 0]}`);
  return parts.length ? parts.join(" · ") : "Aucun constat";
}

export function blockTone(state: string): "succes" | "attention" | "erreur" | "neutre" | "encre" {
  if (["complet", "satisfaits", "coherent_non_authentifie"].includes(state)) return "succes";
  if (["incomplet"].includes(state)) return "erreur";
  if (["a_actualiser", "a_examiner", "indetermines", "non_satisfaits"].includes(state)) return "attention";
  return "neutre";
}

export function SourceLine({ sources }: { sources: SourceRef[] }) {
  if (!sources?.length) return null;
  return (
    <div className="controle-sources">
      {sources.map((s, i) => (
        <Source key={i} doc={s.label} details={[...(s.page ? [`page ${s.page}`] : []), ...(s.detail ? [s.detail] : [])]} />
      ))}
    </div>
  );
}

export function CheckItem({ check, showPerson = true }: { check: Check; showPerson?: boolean }) {
  const outcome = check.details?.resultat as string | undefined;
  return (
    <li className="controle-constat">
      <div className="controle-constat-tete">
        <p className="controle-constat-titre">{check.title}</p>
        <StatusChip status={check.status} label={outcome === "non_satisfait" ? "Non satisfait" : outcome === "indetermine" ? "Indéterminé" : undefined} />
      </div>
      {showPerson && check.personne ? <p className="small muted">{check.personne}</p> : null}
      <p>{check.explanation}</p>
      <SourceLine sources={check.sources} />
    </li>
  );
}

export function CheckList({ checks, showPerson = true, empty = "Aucun constat." }: { checks: Check[]; showPerson?: boolean; empty?: string }) {
  if (!checks.length) return <p className="muted">{empty}</p>;
  return (
    <ul className="controle-constats">
      {checks.map((c) => (
        <CheckItem key={c.id} check={c} showPerson={showPerson} />
      ))}
    </ul>
  );
}

/** Constats conformes repliés par défaut, les autres affichés. */
export function SplitChecks({ checks, showPerson = true }: { checks: Check[]; showPerson?: boolean }) {
  const [open, setOpen] = useState(false);
  const attention = checks.filter((c) => c.status !== "conforme" && c.status !== "info");
  const calm = checks.filter((c) => c.status === "conforme" || c.status === "info");
  return (
    <div className="stack-sm">
      <CheckList checks={attention} showPerson={showPerson} empty="Rien à examiner dans ce résultat." />
      {calm.length ? (
        <div className="controle-cibles">
          <button type="button" className="bv-btn bv-btn-discret bv-btn-petit" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? "Masquer" : "Afficher"} les {calm.length} constats conformes ou informatifs
          </button>
          {open ? <CheckList checks={calm} showPerson={showPerson} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Regroupe des constats par famille, dans l'ordre d'apparition. */
export function byFamily(checks: Check[]): [string, Check[]][] {
  const map = new Map<string, Check[]>();
  for (const c of checks) {
    const k = c.family_label;
    map.set(k, [...(map.get(k) ?? []), c]);
  }
  return [...map.entries()];
}

export function ModesLine({ modes }: { modes?: Modes }) {
  if (!modes) return null;
  const items = Object.entries(modes).filter(([, v]) => v);
  return (
    <p className="row small muted" style={{ flexWrap: "wrap" }}>
      {items.map(([k, v]) => (
        <span key={k} className="row">
          {MODE_LABELS[k] ?? k} : {v!.mode === "production" ? <Badge tone="encre">Service réel</Badge> : <SimBadge mode={v!.mode} />}
        </span>
      ))}
    </p>
  );
}

export function RunLine({ run }: { run: any }) {
  if (!run) return <span className="muted small">Aucune analyse pour l’instant.</span>;
  const triggers: Record<string, string> = { depot: "au dépôt", relance: "relancée par le bailleur", piece: "après une nouvelle pièce ou une correction", demonstration: "démonstration" };
  return (
    <span className="small muted">
      Analyse {triggers[run.trigger] ?? ""} du {date(run.started_at)} · règles <span className="mono">{run.rule_version}</span>
      {run.status === "echec" ? " · analyse interrompue, relancez-la" : ""}
    </span>
  );
}

export function Section({ title, children, actions }: { title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2>{title}</h2>
        {actions ? <div className="card-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export const TAX_RESULTS = [
  { value: "conforme_dernier_avis", label: "Avis indiqué comme dernier avis connu" },
  { value: "non_conforme_ou_remplace", label: "Avis non confirmé (remplacé ou données différentes)" },
  { value: "reference_introuvable", label: "Référence introuvable" },
  { value: "service_indisponible", label: "Service indisponible" },
];

export const SIGNATURE_LABELS: Record<string, [CheckStatus, string]> = {
  valide: ["conforme", "Signature vérifiée"],
  invalide: ["anomalie", "Signature non valide"],
  confiance_indisponible: ["non_concluant", "Certificat non disponible"],
  signature_absente: ["non_concluant", "Signature absente"],
  algorithme_non_pris_en_charge: ["non_concluant", "Algorithme non pris en charge"],
  non_verifiee: ["non_concluant", "Non vérifiée"],
};
